import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import "./SideDrawer.scss";

/**
 * SideDrawer — panel lateral colapsable horizontalmente.
 *
 * Props:
 *   collapsed        (boolean)  — modo controlado: indica si está comprimido/cerrado
 *   onToggle         (fn)       — callback cuando el usuario hace clic en el toggle; recibe el nuevo valor
 *   defaultCollapsed (boolean)  — valor inicial en modo no controlado (default false)
 *   width            (number)   — ancho total en px cuando está expandido (default 320)
 *   modalBreakpoint  (number)   — a partir de qué px de ancho de pantalla (exclusive) el panel
 *                                 se muestra como modal overlay en lugar de inline.
 *                                 Si window.innerWidth < modalBreakpoint → modo modal.
 *                                 Si no se pasa, siempre modo inline.
 *   contextClass     (string)   — clase(s) CSS que se aplican como wrapper del contenido
 *                                 en modo modal, para que los selectores SCSS anidados del
 *                                 componente padre sigan funcionando dentro del portal.
 *                                 Ej: contextClass="ProgramacionDeServicios"
 *   className        (string)
 *   children
 */
const SideDrawer = ({
    children,
    collapsed: controlledCollapsed,
    onToggle,
    defaultCollapsed = false,
    width = 320,
    modalBreakpoint,
    contextClass = "",
    className = "",
}) => {
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
    const [isModal, setIsModal] = useState(
        () => modalBreakpoint != null && window.innerWidth < modalBreakpoint
    );
    const [closing, setClosing] = useState(false);

    const isControlled = controlledCollapsed !== undefined;
    const isCollapsed = isControlled ? controlledCollapsed : internalCollapsed;

    // Detectar cambio de modo al redimensionar
    useEffect(() => {
        if (modalBreakpoint == null) return;
        const fn = () => setIsModal(window.innerWidth < modalBreakpoint);
        window.addEventListener("resize", fn);
        return () => window.removeEventListener("resize", fn);
    }, [modalBreakpoint]);

    // Cerrar modal con Escape
    useEffect(() => {
        if (!isModal || isCollapsed) return;
        const fn = (e) => { if (e.key === "Escape") setClosing(true); };
        document.addEventListener("keydown", fn);
        return () => document.removeEventListener("keydown", fn);
    }, [isModal, isCollapsed]);

    const handleToggle = () => {
        // En modo modal y abierto → iniciar animación de salida primero
        if (isModal && !isCollapsed && !closing) {
            setClosing(true);
            return;
        }
        if (!isControlled) setInternalCollapsed((prev) => !prev);
        onToggle?.(!isCollapsed);
    };

    // Al terminar la animación de salida del modal
    const handleModalAnimationEnd = () => {
        if (closing) {
            setClosing(false);
            if (!isControlled) setInternalCollapsed(true);
            onToggle?.(true);
        }
    };

    // ── Modo modal ────────────────────────────────────────────────
    if (isModal) {
        const showPortal = !isCollapsed || closing;
        return (
            <div className={`SideDrawer sd-mode-modal ${className}`}>
                {/* Botón trigger — reemplaza la tira inline */}
                <button
                    className="sd-modal-trigger"
                    onClick={handleToggle}
                    type="button"
                    aria-label="Abrir panel"
                    title="Abrir panel"
                >
                    <span className="icon" data-icon="chevron_left" aria-hidden="true" />
                </button>

                {showPortal && createPortal(
                    <div
                        className={`sd-overlay ${closing ? "is-closing" : ""}`}
                        onClick={() => setClosing(true)}
                        role="dialog"
                        aria-modal="true"
                    >
                        <div
                            className={`sd-modal-panel animate__animated ${closing ? "animate__slideOutRight" : "animate__slideInRight"}`}
                            style={{ width: `${width}px`, maxWidth: "95vw", "--animate-duration": "0.3s" }}
                            onClick={(e) => e.stopPropagation()}
                            onAnimationEnd={handleModalAnimationEnd}
                        >
                            <button
                                className="sd-modal-close"
                                onClick={() => setClosing(true)}
                                type="button"
                                aria-label="Cerrar panel"
                                title="Cerrar panel"
                            >
                                <span className="icon" data-icon="close" />
                            </button>
                            {contextClass
                                ? <div className={contextClass}>{children}</div>
                                : children
                            }
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        );
    }

    // ── Modo inline ───────────────────────────────────────────────
    return (
        <div
            className={`SideDrawer ${isCollapsed ? "is-collapsed" : ""} ${className}`}
            style={{ "--sd-width": `${width}px` }}
        >
            <div className="sd-panel">
                {/* Track de toggle — ocupa el alto completo, el botón sticky va dentro */}
                <div className="sd-toggle-track">
                    <button
                        className="sd-toggle"
                        onClick={handleToggle}
                        type="button"
                        aria-label={isCollapsed ? "Expandir panel" : "Contraer panel"}
                        title={isCollapsed ? "Expandir panel" : "Contraer panel"}
                    >
                        <span className="icon sd-chevron" data-icon="chevron_right" aria-hidden="true" />
                    </button>
                </div>

                {/* Contenido — se comprime horizontalmente */}
                <div className="sd-body" aria-hidden={isCollapsed || undefined}>
                    {children}
                </div>
            </div>
        </div>
    );
};

export default SideDrawer;
